import { Id } from "@cvx/_generated/dataModel";
import { Play } from "lucide-react";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";

interface CampaignVideosProps {
  videos: string[];
  title?: string;
  className?: string;
}

interface VideoItemProps {
  video: string;
  index: number;
}

function VideoItem({ video, index }: VideoItemProps) {
  // Check if it's a storage ID or URL
  const isStorageId = !video.startsWith("http");

  const { data: videoUrl } = useQuery({
    ...convexQuery(api.applications.getApplicationVideoUrl, {
      storageId: video as Id<"_storage">,
    }),
    enabled: isStorageId,
  });

  const displayUrl = isStorageId ? videoUrl : video;

  // Check if it's a YouTube, Vimeo, or Loom URL for embedded players
  const isYouTube = video.includes("youtube.com") || video.includes("youtu.be");
  const isVimeo = video.includes("vimeo.com");
  const isLoom = video.includes("loom.com");

  if (isYouTube) {
    // Extract YouTube video ID and create embed URL
    let videoId = "";
    if (video.includes("youtu.be/")) {
      videoId = video.split("youtu.be/")[1].split("?")[0];
    } else if (video.includes("youtube.com/watch?v=")) {
      videoId = video.split("v=")[1].split("&")[0];
    }

    if (videoId) {
      return (
        <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full min-h-[300px] max-h-[70vh]">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`Campaign Video ${index + 1}`}
          />
        </div>
      );
    }
  }

  if (isVimeo) {
    // Extract Vimeo video ID and create embed URL
    const vimeoMatch = video.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return (
        <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full min-h-[300px] max-h-[70vh]">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}`}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title={`Campaign Video ${index + 1}`}
          />
        </div>
      );
    }
  }

  if (isLoom) {
    // Handle Loom URLs - if it's already an embed URL, use it directly
    if (video.includes("/embed/")) {
      return (
        <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full min-h-[300px] max-h-[70vh]">
          <iframe
            src={video}
            className="w-full h-full"
            allowFullScreen
            title={`Campaign Video ${index + 1}`}
          />
        </div>
      );
    }
    // Convert regular Loom URLs to embed format if needed
    const loomMatch = video.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    if (loomMatch) {
      const videoId = loomMatch[1];
      return (
        <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full min-h-[300px] max-h-[70vh]">
          <iframe
            src={`https://www.loom.com/embed/${videoId}`}
            className="w-full h-full"
            allowFullScreen
            title={`Campaign Video ${index + 1}`}
          />
        </div>
      );
    }
  }

  // For uploaded video files (storage IDs) or direct video URLs
  if (displayUrl) {
    return (
      <div className="relative rounded-lg overflow-hidden bg-black aspect-video w-full min-h-[300px] max-h-[70vh]">
        <video
          src={displayUrl}
          controls
          preload="metadata"
          className="w-full h-full object-contain rounded-lg"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  // Fallback for loading or error states
  return (
    <div className="relative rounded-lg overflow-hidden bg-muted aspect-video flex items-center justify-center min-h-[300px] w-full">
      <div className="text-center">
        <Play className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading video...</p>
      </div>
    </div>
  );
}

export default function CampaignVideos({
  videos,
  title = "Marketing Videos",
  className = "",
}: CampaignVideosProps) {
  if (!videos || videos.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <h4 className="font-medium text-primary text-base">{title}</h4>
      <div
        className={`grid gap-6 ${
          videos.length === 1
            ? "grid-cols-1"
            : videos.length === 2
              ? "grid-cols-1 lg:grid-cols-2 justify-items-center"
              : "grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 justify-items-center"
        }`}
      >
        {videos.map((video, index) => (
          <div
            key={`${video}-${index}`}
            className={`space-y-2 flex flex-col items-center w-full ${
              videos.length === 1 ? "max-w-none" : "max-w-2xl"
            }`}
          >
            <VideoItem video={video} index={index} />
          </div>
        ))}
      </div>
    </div>
  );
}
